import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { NavBar } from '../components/NavBar'
import { useProfileDiaryEntries, deleteLogEntry } from '../hooks/useLogEntries'
import { DiaryEntry, ProductionType } from '../types'
import { Button } from '../components/ui/button'
import { GoalTracker } from '../components/GoalTracker'

const PRODUCTION_TYPE_LABELS: Record<ProductionType, string> = {
  live: 'Live performance',
  filmed_stage: 'Filmed stage production',
  feature_film: 'Feature film',
  television: 'Television',
  audio: 'Audio / Radio',
  immersive: 'Immersive / site-specific',
  adaptation: 'Adaptation',
}

const STAR_PATH =
  'M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z'

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function StarDisplay({ rating }: { rating: number | null }) {
  if (rating == null) return null
  return (
    <div
      role="img"
      aria-label={`${rating} out of 5 stars`}
      className="flex gap-0.5"
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill={star <= rating ? '#b45309' : 'none'}
          stroke={star <= rating ? '#b45309' : '#57534e'}
          strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={STAR_PATH} />
        </svg>
      ))}
    </div>
  )
}

function EntryPhoto({ entry }: { entry: DiaryEntry }) {
  const userPhoto = entry.user_photo_url
  const poster = entry.productions?.poster_image_url

  if (userPhoto) {
    return (
      <img
        src={userPhoto}
        alt="Performance photo"
        className="h-20 w-20 flex-shrink-0 rounded object-cover"
      />
    )
  }

  if (poster) {
    return (
      <img
        src={poster}
        alt="Production poster"
        className="h-20 w-20 flex-shrink-0 rounded object-cover"
      />
    )
  }

  return (
    <div
      data-testid="photo-placeholder"
      className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded bg-stone-800"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-8 w-8 text-stone-600"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
        />
      </svg>
    </div>
  )
}

function DiaryEntryCard({ entry, onDelete }: { entry: DiaryEntry; onDelete: (id: string) => void }) {
  const production = entry.productions
  const play = production?.plays
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)

  async function handleDeleteConfirm() {
    setDeleting(true)
    const { error } = await deleteLogEntry(entry.id)
    setDeleting(false)
    if (error) {
      setConfirming(false)
    } else {
      onDelete(entry.id)
    }
  }

  return (
    <article className="flex gap-4 border-b border-stone-800 py-5">
      <EntryPhoto entry={entry} />

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-serif text-base font-semibold text-stone-100">
            {play?.title ?? 'Unknown play'}
          </span>
        </div>

        <p className="text-sm text-stone-400">
          {production?.company_or_studio ?? production?.title ?? play?.title ?? 'Unknown production'}
          {production?.year ? ` (${production.year})` : ''}
        </p>

        <p className="text-xs text-stone-500">
          {production?.type ? PRODUCTION_TYPE_LABELS[production.type] : ''}
        </p>

        <div className="flex items-center gap-3 pt-0.5">
          {entry.rating != null && <StarDisplay rating={entry.rating} />}
          <span className="text-xs text-stone-500">{formatDate(entry.date_watched)}</span>
        </div>

        {entry.review_text && (
          <p className="pt-1 text-sm leading-relaxed text-stone-300">
            {entry.review_text}
          </p>
        )}

        <div className="flex items-center gap-3 pt-2">
          {confirming ? (
            <>
              <span className="text-xs text-stone-400">Delete this entry?</span>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="text-xs text-red-400 transition-colors hover:text-red-300 disabled:opacity-60"
              >
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="text-xs text-stone-500 transition-colors hover:text-stone-300"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <Link
                to={`/log?edit=${entry.id}`}
                className="text-xs text-stone-500 transition-colors hover:text-stone-300"
              >
                Edit
              </Link>
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="text-xs text-stone-500 transition-colors hover:text-stone-400"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  )
}

export function DiaryPage() {
  const { user } = useAuth()
  const {
    entries,
    loading: entriesLoading,
    error: entriesError,
    hasMore,
    loadMore,
    removeEntry,
  } = useProfileDiaryEntries(user?.id)

  const isInitialLoad = entriesLoading && entries.length === 0

  return (
    <div className="min-h-screen bg-stone-900 text-stone-100">
      <NavBar />
      <div className="px-6 py-10">
      <div className="mx-auto max-w-2xl">
        <header className="mb-8">
          <h1 className="font-serif text-3xl font-semibold tracking-tight">Diary</h1>
        </header>

        <GoalTracker />

        <div className="mb-8">
          <Link to="/log">
            <Button className="bg-red-900 px-6 py-2 text-sm font-semibold text-stone-100 hover:bg-red-800">
              Log a performance
            </Button>
          </Link>
        </div>

        {isInitialLoad ? (
          <div className="space-y-4 pt-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-4 border-b border-stone-800 py-5">
                <div className="h-20 w-20 flex-shrink-0 rounded bg-stone-800" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-40 rounded bg-stone-800" />
                  <div className="h-3 w-32 rounded bg-stone-800" />
                  <div className="h-3 w-20 rounded bg-stone-800" />
                </div>
              </div>
            ))}
          </div>
        ) : entriesError ? (
          <p className="py-8 text-center text-sm text-red-400">
            Failed to load diary: {entriesError}
          </p>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-stone-400">You haven't logged any performances yet.</p>
            <Link
              to="/log"
              className="mt-3 inline-block text-sm text-stone-500 hover:text-stone-300 transition-colors"
            >
              Log your first →
            </Link>
          </div>
        ) : (
          <>
            <div>
              {entries.map((entry) => (
                <DiaryEntryCard key={entry.id} entry={entry} onDelete={removeEntry} />
              ))}
            </div>

            {hasMore && (
              <div className="mt-6 text-center">
                <Button
                  variant="ghost"
                  onClick={loadMore}
                  disabled={entriesLoading}
                  className="text-stone-400 hover:text-stone-200"
                >
                  {entriesLoading ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  )
}
